import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import produce from 'immer';

import { PERMISSIONS_MAP, USER_INITIAL_PERMISSION } from '../../constants/service/common/userPermissions';
import {
  PermissionAction,
  PermissionsMapType,
  PermissionSubjects,
  PermissionTableRowType,
  UserPermissionType,
} from '../../types/common/PermissionType';
import { PERMISSION_TABLE_MODEL } from '../../views/account/admin/common/constants/menu';
import { FormMode } from '../../views/account/admin/common/types/AccountAdminFormType';

interface IUsePermissions {
  userPermissions?: UserPermissionType;
  permissionTableData: PermissionTableRowType[];
  handleUpdatePermission: (key: PermissionSubjects, initialValue: PermissionAction[], checked: boolean) => void;
}

const usePermissions = (mode: string, permissions: UserPermissionType): IUsePermissions => {
  const [userPermissions, setUserPermissions] = useState(
    mode !== FormMode.CREATE ? permissions : USER_INITIAL_PERMISSION,
  );

  const { setValue } = useFormContext();

  const [permissionTableData, setPermissionTableData] = useState<PermissionTableRowType[]>(PERMISSION_TABLE_MODEL);

  // 유저의 권한 정보를 바탕으로 테이블에 동적으로 체크 상태를 추가함.
  const applyUserPermissionToRow = (row: PermissionTableRowType): PermissionTableRowType => {
    let selected = false;

    const permissionsMapKeys = PERMISSIONS_MAP[row.key as keyof PermissionsMapType];

    if (permissionsMapKeys) {
      // 서브로우의 체크 상태에 따라 최상위 요소의 indeterminate를 정확히 판별하기 위해 메서드를 some 매서드를 적용.
      // 하위 요소 중 하나라도 체크가 되면 select 상태를 전달하고, 이후 테이블에서 isSomeSelect와 isAllSelect에 여부에 따라 indeterminate가 적용
      selected = permissionsMapKeys.every((permissionSubject) => {
        if (userPermissions) {
          const subPermission = userPermissions[permissionSubject as PermissionSubjects];
          return subPermission ? Object.values(subPermission).every(Boolean) : false;
        }
      });
    } else {
      if (userPermissions) {
        selected = userPermissions[row.key as PermissionSubjects]
          ? row.permissions
            ? (row.permissions as PermissionAction[]).some((permissionAction: PermissionAction) => {
                return userPermissions[row.key as PermissionSubjects]?.[permissionAction] === true;
              })
            : false
          : false;
      }
    }
    return {
      ...row,
      selected,
      subRows: row.subRows?.map(applyUserPermissionToRow),
    };
  };

  const updateTopLevelMenuPermission = (
    originalPermissions: UserPermissionType,
    key: string,
    checked: boolean,
  ): UserPermissionType => {
    return produce(originalPermissions, (draftPermissions: UserPermissionType) => {
      if (!draftPermissions[key]) {
        draftPermissions[key] = { ...USER_INITIAL_PERMISSION[key] };
      }

      const newValue = draftPermissions[key];

      if (PERMISSIONS_MAP[key]) {
        PERMISSIONS_MAP[key].forEach((subKey) => {
          if (subKey in newValue) {
            newValue[subKey as PermissionAction] = checked;
          }
        });
      } else {
        for (const permissionManage in PERMISSIONS_MAP) {
          if (PERMISSIONS_MAP[permissionManage].includes(key as PermissionSubjects)) {
            for (const subPermission in USER_INITIAL_PERMISSION[key]) {
              newValue[subPermission as PermissionAction] = checked;
            }
          }
        }
      }
    });
  };

  const updateSubrowPermission = (
    originalPermissions: UserPermissionType,
    key: PermissionSubjects,
    value: PermissionAction[],
    checked: boolean,
  ) => {
    return produce(originalPermissions, (draftPermissions) => {
      if (!draftPermissions[key]) {
        draftPermissions[key] = {};
      }

      const newValue = draftPermissions[key];

      value.forEach((permissionAction) => {
        newValue[permissionAction] = checked;
      });
    });
  };

  const handleUpdatePermission = useCallback((key: PermissionSubjects, value: PermissionAction[], checked: boolean) => {
    setUserPermissions((prevPermissions: UserPermissionType) => {
      if (PERMISSIONS_MAP[key]) {
        return PERMISSIONS_MAP[key].reduce((updatedPermissions, subKey) => {
          return updateTopLevelMenuPermission(updatedPermissions, subKey, checked);
        }, prevPermissions);
      } else {
        return updateSubrowPermission(prevPermissions, key, value, checked);
      }
    });
  }, []);

  useEffect(() => {
    setPermissionTableData(PERMISSION_TABLE_MODEL.map(applyUserPermissionToRow));
    setValue('permission', userPermissions);
  }, [userPermissions]);

  return {
    userPermissions,
    permissionTableData,
    handleUpdatePermission,
  };
};

export default usePermissions;
